import { Component, Input, OnInit, inject, input } from '@angular/core';
import { EnvironmentService } from '../../../services/environment.service';
import { PaymentMode, UserPaymentModeService } from '../../../services/user/user-payment-mode.service';
import { filter, map, tap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { FiltersService } from '../../../services/filters.service';
import { BankingService } from '../../../services/banking.service';
import { SeoService } from '../../../services/seo.service';
import { DefaultImageDirective } from '../../directives/default-image.directive';
import { AsyncPipe } from '@angular/common';
import { CustomFlowData } from '../../interfaces/custom-flow-data-interface';

@Component({
    selector: 'app-payment-images',
    templateUrl: './payment-images.component.html',
    styleUrls: ['./payment-images.component.scss'],
    imports: [DefaultImageDirective, AsyncPipe]
})
export class PaymentImagesComponent implements OnInit {
  private _env = inject(EnvironmentService);
  private _filters = inject(FiltersService);
  private _banking = inject(BankingService);
  seo = inject(SeoService);
  paymentMode = inject(UserPaymentModeService);


  @Input() customFlow: CustomFlowData;
  readonly isLoadFromBanking = input<boolean>(undefined);

  public paymentImages: any[] | string;

  public bankingPayments$: Observable<any>;

  private _baseCryptoPath = `/assets/svg/crypto-symbols`;
  private _baseFiatPath = `/assets/img/mode/${ PaymentMode.FIAT }/payments`;

  ngOnInit(): void {
    this._resolvePaymentImages();
  }

  private _resolvePaymentImages() {
    combineLatest([
      this._env.env$,
      this.paymentMode.currentMode$
    ]).pipe(
      tap(([env, mode]) => {
        if (mode === PaymentMode.CRYPTO) {
          this.paymentImages = this._resolveCryptoImagesByGeo(env);
        } else {
          this.paymentImages = this._resolvePaymentImagesByGeo(env);
        }
      })
    ).subscribe();

    if (this.isLoadFromBanking()) {
      this.bankingPayments$ = this._banking.depositPaymentList$.pipe(
        filter((data) => !!data[0]),
        map((data) => {
          return data.filter((e) => !e?.IsCrypto)?.slice(0, 4);
        }),
      );
    }
  }

  private _resolvePaymentImagesByGeo(env: any): any[] {
    const country = env.data.country.short;
    switch (country) {
      case 'de':
        return [
          {class: 'sofort', image: `${ this._baseFiatPath }/${ country }/sofort.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/visa.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/master.svg`},
          {class: 'mifinity', image: `${ this._baseFiatPath }/${ country }/mifinity.svg`},
        ];
      case 'au':
      case 'nz':
        return [
          {class: '', image: `${ this._baseFiatPath }/${ country }/visa.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/master.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/neosurf.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/poli.svg`},
        ];
      case 'ca':
        return [
          {class: '', image: `${ this._baseFiatPath }/${ country }/interac.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/visa.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/master.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/flexepin.svg`},
        ];
      case 'br':
        return [
          {class: '', image: `${ this._baseFiatPath }/${ country }/pix.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/boleto.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/play4fun.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/visa.svg`},
        ];
      case 'no':
        return [
          {class: '', image: `${ this._baseFiatPath }/${ country }/visa.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/master.svg`},
          {class: 'mifinity', image: `${ this._baseFiatPath }/${ country }/mifinity.svg`},
          {class: 'banktransfer', image: `${ this._baseFiatPath }/${ country }/banktransfer.svg`},
        ];
      case 'at':
        return [
          {class: 'sofort', image: `${ this._baseFiatPath }/${ country }/sofort.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/paysafecard.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/rapid.svg`},
          {class: '', image: `${ this._baseFiatPath }/${ country }/skrill.svg`},
        ];
      default:
        return [
          {class: '', image: `${ this._baseFiatPath }/default/visa.svg`},
          {class: '', image: `${ this._baseFiatPath }/default/master.svg`},
          {class: 'banktransfer', image: `${ this._baseFiatPath }/default/banktransfer.svg`},
          {class: '', image: `${ this._baseFiatPath }/default/neosurf.svg`},
        ];
    }
  }
  private _resolveCryptoImagesByGeo(env: any): any[] {
    const country = env.data.country.short;
    switch (country) {
      default:
        return this._filters.orderBySegment([
          {class: '', image: `${ this._baseCryptoPath }/bch.svg`, text: 'BCH', slug: 'BCH_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/eth.svg`, text: 'ETH', slug: 'ETH_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/btc.svg` , text: 'BTC', slug: 'BTC_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/ltc.svg`, text: 'LTC', slug: 'LTC_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/dog.svg`, text: 'DOG', slug: 'DOG_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/usdt.svg`, text: 'USDT', slug: 'USDT_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/xrp.svg`, text: 'XRP', slug: 'XRP_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/ada.svg`, text: 'ADA', slug: 'ADA_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/trx.svg`, text: 'TRX', slug: 'TRX_coinspaid'},
          {class: '', image: `${ this._baseCryptoPath }/bnb.svg`, text: 'BNB', slug: 'BNB_coinspaid'},
        ], this._banking.orderPaymentsCrypto, 'slug');
    }
  }

}
